import { AxiosError } from 'axios';
import { ServerResponse, WithAuditDetails } from 'data/types';
import { History } from 'history';
import { Dispatch, Action } from 'redux';
import { RootState } from 'data/root.reducers';
import i18n from 'i18n';
import { showSuccessMessage, showError } from 'shared/toaster';
import { ThunkDispatch } from 'redux-thunk';
import { extractErrorMessage } from '../../utils';
import * as api from './filterGroup.api';
import { getSelectedFilterGroup } from './filterGroup.selectors';
import {
  GET_FILTER_GROUP_LIST,
  GET_FILTER_GROUP,
  CREATE_FILTER_GROUP,
  UPDATE_FILTER_GROUP,
  DELETE_FILTER_GROUP,
  GO_TO_FILTER_GROUP_LIST,
  GO_TO_FILTER_GROUP_EDIT,
  GO_TO_FILTER_GROUP_VIEW,
  GO_TO_FILTER_GROUP_CREATE,
  SELECT_FILTER_GROUP,
  DeleteFilterGroupThunkAction,
  UpdateFilterGroupThunkAction,
  CreateFilterGroupThunkAction,
  SET_FILTER_GROUP_LOADING,
  SetFilterGroupLoadingAction,
  FilterGroupActionTypes,
  FilterGroup,
  FilterGroupPayload,
  OPEN_FILTER_GROUP_CREATE_POPUP,
  CLOSE_FILTER_GROUP_CREATE_POPUP,
  SelectFilterGroupAction,
  OpenFilterGroupCreatePopupAction,
  CloseFilterGroupCreatePopupAction,
} from './filterGroup.types';
import { FILTER_GROUP, SYSTEM_CONFIGURATION_ROUTE, FILTER_SETTING } from '../../constants';

export function openFilterGroupCreatePopup(filterGroup?: FilterGroup): OpenFilterGroupCreatePopupAction {
  return {
    type: OPEN_FILTER_GROUP_CREATE_POPUP,
    payload: { filterGroup },
  };
}

export function closeFilterGroupCreatePopup(): CloseFilterGroupCreatePopupAction {
  return {
    type: CLOSE_FILTER_GROUP_CREATE_POPUP,
  };
}

export function goToFilterGroupList(history: History): (dispatch: Dispatch) => void {
  return (dispatch: Dispatch) => {
    dispatch({
      type: GO_TO_FILTER_GROUP_LIST,
    });

    history.push(`${SYSTEM_CONFIGURATION_ROUTE}${FILTER_SETTING}${FILTER_GROUP}`);
  };
}

export function goToFilterGroupCreate(history: History): (dispatch: Dispatch) => void {
  return (dispatch: Dispatch) => {
    dispatch({
      type: GO_TO_FILTER_GROUP_CREATE,
    });

    history.push(`${SYSTEM_CONFIGURATION_ROUTE}${FILTER_SETTING}${FILTER_GROUP}/create`);
  };
}

export function goToFilterGroupEdit(history: History): (dispatch: Dispatch) => void {
  return (dispatch: Dispatch) => {
    dispatch({
      type: GO_TO_FILTER_GROUP_EDIT,
    });

    history.push(`${SYSTEM_CONFIGURATION_ROUTE}${FILTER_SETTING}${FILTER_GROUP}/edit`);
  };
}

export function goToFilterGroupView(history: History): (dispatch: Dispatch) => void {
  return (dispatch: Dispatch) => {
    dispatch({
      type: GO_TO_FILTER_GROUP_VIEW,
    });

    history.push(`${SYSTEM_CONFIGURATION_ROUTE}${FILTER_SETTING}${FILTER_GROUP}/view`);
  };
}

export function setFilterGroupLoadingAction(
  isLoading: boolean,
): SetFilterGroupLoadingAction {
  return {
    type: SET_FILTER_GROUP_LOADING,
    payload: { isLoading },
  };
}

export function getFilterGroupList(): FilterGroupActionTypes {
  return {
    type: GET_FILTER_GROUP_LIST,
    payload: api.getFilterGroupList(),
  };
}

export function getFilterGroup(
  id: string,
): FilterGroupActionTypes {
  return {
    type: GET_FILTER_GROUP,
    payload: api.getFilterGroup(id),
  };
}

export function createFilterGroup(
  body: WithAuditDetails<FilterGroup>,
  history: History,
): CreateFilterGroupThunkAction {
  return async (dispatch: ThunkDispatch<RootState, unknown, Action<string>>) => {
    try {
      await dispatch({
        type: CREATE_FILTER_GROUP,
        payload: api.createFilterGroup(body),
      });

      dispatch(getFilterGroupList());
      dispatch(goToFilterGroupList(history));
      showSuccessMessage(i18n.t('filterGroup.create.success', { name: body.data?.fgId }), 5000);

    } catch (e) {
      const errorMessage = extractErrorMessage(e as AxiosError<ServerResponse<void>>);
      showError(i18n.t('filterGroup.create.failed', { name: body.data?.fgId, error: errorMessage }));
    }
  };
}

export function createFilterGroupFromForm(
  body: WithAuditDetails<FilterGroup>,
): CreateFilterGroupThunkAction {
  return async (dispatch: ThunkDispatch<RootState, unknown, Action<string>>) => {
    dispatch(setFilterGroupLoadingAction(true));
    try {
      await dispatch({
        type: CREATE_FILTER_GROUP,
        payload: api.createFilterGroup(body),
      });

      dispatch(closeFilterGroupCreatePopup());
      showSuccessMessage(i18n.t('filterGroup.create.success', { name: body.data?.fgId }), 5000);

    } catch (e) {
      const errorMessage = extractErrorMessage(e as AxiosError<ServerResponse<void>>);
      showError(i18n.t('filterGroup.create.failed', { name: body.data?.fgId, error: errorMessage }));

    } finally {
      dispatch(setFilterGroupLoadingAction(false));
      dispatch(getFilterGroupList());
    }
  };
}

export function updateFilterGroup(
  id: string,
  body: WithAuditDetails<FilterGroupPayload>,
  history: History,
): UpdateFilterGroupThunkAction {
  return async (dispatch: ThunkDispatch<RootState, unknown, Action<string>>) => {
    try {
      await dispatch({
        type: UPDATE_FILTER_GROUP,
        payload: api.updateFilterGroup(id, body),
      });

      dispatch(getFilterGroupList());
      dispatch(goToFilterGroupList(history));
      showSuccessMessage(i18n.t('filterGroup.update.success', { name: body.data?.fgId }), 5000);

    } catch (e) {
      const errorMessage = extractErrorMessage(e as AxiosError<ServerResponse<void>>);
      showError(i18n.t('filterGroup.update.failed', { name: body.data?.fgId, error: errorMessage }));
    }
  };
}

export function deleteFilterGroup(
  id: string,
  name: string,
  history?: History,
): DeleteFilterGroupThunkAction {
  return async (dispatch: ThunkDispatch<RootState, unknown, Action<string>>, getState: () => RootState) => {
    const [selectedFilterGroup] = getSelectedFilterGroup(getState());

    try {
      await dispatch({
        type: DELETE_FILTER_GROUP,
        payload: api.deleteFilterGroup(id, name),
      });

      showSuccessMessage(i18n.t('filterGroup.delete.success', { name: selectedFilterGroup?.fgId }), 5000);
      if (history != null) {
        dispatch(goToFilterGroupList(history));
      }
    } catch (e) {
      const errorMessage = extractErrorMessage(e as AxiosError<ServerResponse<void>>);
      showError(i18n.t('filterGroup.delete.failed', { name: selectedFilterGroup?.fgId, error: errorMessage }));

    } finally {
      dispatch(getFilterGroupList());
    }
  };
}

export function selectFilterGroup(
  filterGroup: FilterGroup[],
): SelectFilterGroupAction {
  return {
    type: SELECT_FILTER_GROUP,
    payload: filterGroup,
  };
}
